import { useState } from "react";
import useTranslation from "next-translate/useTranslation";
import styles from "./ChallengePopup.module.css";
import Popup from "../../components/Popup";

import { useLobby } from "../../ducks/lobby";
import { useUser } from "../../ducks/user";
import {
  NewChallenge,
  sendPrivateChallenge,
} from "../shared/challengeController";
import SegmentedControl from "../../components/SegmentedControl";
import DailyOptions from "../../components/DailyOptions";
import LiveOptions from "../../components/LiveOptions";
import { TimeControl } from "@chesshotel/constants";
import { getModeId } from "@/src/util/util";
import { useRouter } from "next/router";
import { addSentChallenge } from "@/src/ducks/challenges";
import { api } from "@/src/services/api";
import DailyNotMember from "../lobby/daily/DailyNotMember";

export async function sendOpenPrivateChallenge(challenge: NewChallenge) {
  const res = await api.post("/challenge", challenge);
  return res.data;
}

export default function ChallengePopup({ friend, onClose }) {
  const { t } = useTranslation("common");

  const { id, username, isGuest } = useUser();

  const { time, inc, variant, timeControl, turnLength } = useLobby();

  const [chosenTime, setChosenTime] = useState(time);
  const [chosenInc, setChosenInc] = useState(inc);
  const [chosenVariant, setChosenVariant] = useState(variant);
  const [chosenTimeControl, setTimeControl] = useState(timeControl);
  const [chosenTurnLength, setTurnLength] = useState(turnLength);

  const [showDailyPopup, setShowDailyPopup] = useState(false);

  const router = useRouter();

  const playerName =
    friend?.username.substring(0, 2) === "G*"
      ? friend?.username.substring(2)
      : friend?.username;

  const challenge: NewChallenge = {
    userId: id,
    username: username,
    opponentUsername: friend?.username,
    opponentId: friend?.id,
    timeControl: chosenTimeControl,
    time: chosenTimeControl == TimeControl.LIVE ? chosenTime : null,
    inc: chosenTimeControl == TimeControl.LIVE ? chosenInc : null,
    variantId: chosenVariant,
    turnLength: chosenTimeControl == TimeControl.LIVE ? null : chosenTurnLength,
    modeId: getModeId(chosenTime, chosenVariant, chosenTimeControl),
    leaguePlay: false,
  };

  return (
    <Popup
      title={`${t("Invite")} ${friend ? playerName : t("a friend")}`}
      top
      onClose={onClose}
    >
      {showDailyPopup && (
        <Popup title={t("Daily")} top onClose={() => setShowDailyPopup(false)}>
          <DailyNotMember />
          <div className="flex items-center justify-end mt-8">
            <button
              className="btn btn-base btn-outline"
              onClick={() => setShowDailyPopup(false)}
            >
              {t("Cancel")}
            </button>
          </div>
        </Popup>
      )}
      <label className={styles.label}>{t("Variant")}</label>
      <SegmentedControl
        options={[t("Standard"), "Chess960"]}
        numbersAsOptions={true}
        startNumber={1}
        selected={chosenVariant}
        setSelected={setChosenVariant}
        selectedBgColor="bg-altblue-500"
        selectedTextColor="text-white"
        unselectedBgColor="bg-altgray-200"
        unselectedTextColor="text-altgray-700"
        customClassNameOuterDiv="border-4 border-altgray-200 bg-altgray-200 rounded-md"
        customClassNameInnerDiv="p-1 rounded-md shadow-xl no-select"
      />
      <div className="mt-5">
        <label className={styles.label}>{t("Time control")}</label>
        <SegmentedControl
          options={[t("Live"), t("Daily")]}
          numbersAsOptions={true}
          startNumber={1}
          selected={chosenTimeControl}
          setSelected={(val) => {
            if (isGuest && val == TimeControl.DAILY) {
              setShowDailyPopup(true);
            } else {
              setTimeControl(val);
            }
          }}
          selectedBgColor="bg-altblue-500"
          selectedTextColor="text-white"
          unselectedBgColor="bg-altgray-200"
          unselectedTextColor="text-altgray-700"
          customClassNameOuterDiv="border-4 rounded-md border-altgray-200 bg-altgray-200"
          customClassNameInnerDiv="p-1 rounded-md shadow-xl"
        />
      </div>
      {chosenTimeControl === TimeControl.LIVE && (
        <LiveOptions
          setTime={setChosenTime}
          time={chosenTime}
          setInc={setChosenInc}
          inc={chosenInc}
          styles={styles}
        />
      )}
      {chosenTimeControl === TimeControl.DAILY && (
        <DailyOptions
          turnLength={chosenTurnLength}
          setTurnLength={setTurnLength}
          styles={styles}
        />
      )}
      <div className="flex items-center justify-end mt-8">
        <button className="btn btn-base btn-outline" onClick={onClose}>
          {t("Cancel")}
        </button>
        <button
          className={"btn btn-base btn-blue ml-3"}
          onClick={async () => {
            if (friend) {
              const challengeDb = await sendPrivateChallenge(challenge);
              router.push("/c/" + challengeDb.slug);
              onClose();
            } else {
              const challengeDb = await sendOpenPrivateChallenge(challenge);
              addSentChallenge(challengeDb);
              onClose();
              router.push("/challenge/" + challengeDb.slug);
            }
          }}
        >
          {friend ? t("Send") : t("Continue")}
        </button>
      </div>
    </Popup>
  );
}
